<script setup lang="ts">
import type { OAuthLicensesResponse } from "~/server/utils/oauth/oauth.api.utils";
import {
  getCompanionSuggestedCurriculumLevels,
  hasCompanionUsableCurriculumLicense,
} from "~/utils/companion-suggested-curriculum";
import type { Level } from "~/types/Level";

const props = withDefaults(
  defineProps<{
    title?: string;
    description?: string;
    cancelLabel?: string;
    onCancel?: () => void | Promise<void>;
    onSaved?: () => void | Promise<void>;
  }>(),
  {
    title: "Choose Curriculum",
    cancelLabel: "Cancel",
  }
);

const levelsStore = useLevels();
const { allLevels, productCategoryLevels } = storeToRefs(levelsStore);

const categoriesStore = useIEWProductCategories();
const { publicIEWProductCategories } = storeToRefs(categoriesStore);

const companionStore = useCompanion();
const { profiles, selectedProfile, selectedProfileId } =
  storeToRefs(companionStore);

const searchQuery = ref("");
const selectedLevelId = ref<string | undefined>();
const isSaving = ref(false);
const isLoadingCurriculumOptions = ref(true);
const activeLicenses = ref<OAuthLicensesResponse["licenses"]>([]);
const transferredLicenseAssignmentIds = ref<number[]>([]);

const assignedLicenseAssignmentIds = computed(() => {
  return new Set(
    [
      ...profiles.value
        .filter((profile) => profile.id !== selectedProfileId.value)
        .map((profile) => profile.licenseAssignmentId)
        .filter((licenseAssignmentId): licenseAssignmentId is number => {
          return licenseAssignmentId != undefined;
        }),
      ...transferredLicenseAssignmentIds.value,
    ]
  );
});

const suggestedLevels = computed(() => {
  return getCompanionSuggestedCurriculumLevels({
    levels: allLevels.value,
    licenses: activeLicenses.value,
    assignedLicenseAssignmentIds: assignedLicenseAssignmentIds.value,
  });
});

const shouldShowSuggestedLevels = computed(() => {
  return searchQuery.value.trim() === "" && suggestedLevels.value.length > 0;
});

const categoryLevelMap = computed(() => {
  return publicIEWProductCategories.value.map((category) => {
    const levels = productCategoryLevels
      .value(category.id)
      .filter((level) =>
        (level.name ?? "")
          .toLowerCase()
          .includes(searchQuery.value.toLowerCase())
      );

    const includeCategory =
      searchQuery.value.trim() === "" ||
      category.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      levels.length > 0;

    return {
      category,
      levels: searchQuery.value.trim()
        ? levels
        : productCategoryLevels.value(category.id),
      includeCategory,
    };
  });
});

const visibleCategoryLevelMap = computed(() => {
  return categoryLevelMap.value.filter((entry) => entry.includeCategory);
});

const selectedLevel = computed(() => {
  return allLevels.value.find((level) => level.id === selectedLevelId.value);
});

const categoryTitleMap = computed(() => {
  return new Map(
    publicIEWProductCategories.value.map((category) => [
      category.id,
      category.title,
    ])
  );
});

const getLevelCategoryTitle = (categoryId: string) => {
  return categoryTitleMap.value.get(categoryId) ?? "IEW Curriculum";
};

const isLevelSelected = (levelId: string) => {
  return selectedLevelId.value === levelId;
};

const selectLevel = (levelId: string) => {
  selectedLevelId.value = levelId;
};

const hasUsableLicenseForLevel = (level: Level) => {
  return hasCompanionUsableCurriculumLicense({
    level,
    licenses: activeLicenses.value,
    assignedLicenseAssignmentIds: assignedLicenseAssignmentIds.value,
    selectedProfileLicenseAssignmentId:
      selectedProfile.value?.licenseAssignmentId,
  });
};

const cancel = async () => {
  if (props.onCancel) {
    await props.onCancel();
  }
};

const saveCurriculum = async () => {
  if (!selectedLevelId.value) {
    useBaseToast("Please select a curriculum.", "error");
    return;
  }

  isSaving.value = true;

  try {
    const didSave = await companionStore.updateProfileCurriculum(
      selectedLevelId.value
    );

    if (!didSave) return;

    useBaseToast("Curriculum saved.", "success");

    if (props.onSaved) {
      await props.onSaved();
    }
  } catch (e) {
    useBaseToast("Oops! Unable to save curriculum.", "error");
  } finally {
    isSaving.value = false;
  }
};

const loadActiveLicenses = async () => {
  try {
    const response = await $fetch<
      OAuthLicensesResponse & { transferredLicenseAssignmentIds?: number[] }
    >(
      "/api/companion/licenses",
      {
        headers: await useApiHeaders(),
      }
    );

    activeLicenses.value = response.licenses ?? [];
    transferredLicenseAssignmentIds.value =
      response.transferredLicenseAssignmentIds ?? [];
  } catch (e) {
    activeLicenses.value = [];
    transferredLicenseAssignmentIds.value = [];
  }
};

onMounted(async () => {
  try {
    await Promise.all([
      categoriesStore.initializeIEWProductCategories(),
      allLevels.value.length === 0
        ? levelsStore.initializeLevels()
        : Promise.resolve(),
      loadActiveLicenses(),
    ]);

    selectedLevelId.value =
      selectedProfile.value?.curriculumLevelId ?? suggestedLevels.value[0]?.id;
  } finally {
    isLoadingCurriculumOptions.value = false;
  }
});
</script>

<template>
  <div class="flex flex-col h-[85vh] w-[min(92vw,760px)]">
    <div
      class="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4"
    >
      <div>
        <h2 class="text-xl font-bold text-gray-900">{{ title }}</h2>
      </div>
    </div>

    <div class="px-6 py-4 border-b border-gray-100">
      <BaseSearchInput
        v-model="searchQuery"
        placeholder="Search curriculum..."
        class="w-full"
      />
    </div>

    <div class="px-6 py-4 overflow-auto min-h-0 space-y-6 grow">
      <div v-if="isLoadingCurriculumOptions" class="space-y-4">
        <div
          class="rounded-xl border border-gray-100 bg-gray-50/70 p-4 space-y-3"
        >
          <div class="h-3 w-40 rounded bg-gray-200 animate-pulse"></div>
          <div
            v-for="i in 2"
            :key="i"
            class="p-4 rounded-xl border border-gray-100 bg-white flex items-center gap-4"
          >
            <div
              class="w-11 h-11 rounded-lg bg-gray-100 animate-pulse shrink-0"
            ></div>
            <div class="min-w-0 flex-1 space-y-2">
              <div class="h-4 w-2/3 rounded bg-gray-100 animate-pulse"></div>
              <div class="h-3 w-1/3 rounded bg-gray-100 animate-pulse"></div>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <div class="h-px bg-gray-100 flex-1"></div>
          <div class="h-3 w-28 rounded bg-gray-100 animate-pulse"></div>
          <div class="h-px bg-gray-100 flex-1"></div>
        </div>

        <div class="space-y-3">
          <div class="h-3 w-32 rounded bg-gray-200 animate-pulse"></div>
          <div
            v-for="i in 3"
            :key="i"
            class="p-4 rounded-xl border border-gray-100 flex items-center gap-4"
          >
            <div
              class="w-11 h-11 rounded-lg bg-gray-100 animate-pulse shrink-0"
            ></div>
            <div class="min-w-0 flex-1 space-y-2">
              <div class="h-4 w-3/4 rounded bg-gray-100 animate-pulse"></div>
              <div class="h-3 w-1/4 rounded bg-gray-100 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      <section
        v-else-if="shouldShowSuggestedLevels"
        class="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3"
      >
        <h3 class="text-xs font-bold text-primary uppercase tracking-wide px-1">
          SUGGESTED
        </h3>

        <div class="space-y-2">
          <div
            v-for="level in suggestedLevels"
            :key="level.id"
            class="p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-sm flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between group bg-white"
            :class="
              isLevelSelected(level.id!)
                ? 'border-primary bg-white ring-1 ring-primary/20'
                : 'border-primary/10 hover:border-primary/40 hover:bg-white'
            "
            @click="selectLevel(level.id!)"
          >
            <div class="flex w-full items-center gap-4 min-w-0 sm:flex-1">
              <div
                class="w-11 h-11 rounded-lg flex-shrink-0 flex items-center justify-center transition-colors"
                :class="
                  isLevelSelected(level.id!)
                    ? 'bg-primary text-white'
                    : 'bg-primary/10 text-primary group-hover:bg-primary/15'
                "
              >
                <BaseIconListChecks class="w-5 h-5" />
              </div>
              <div class="min-w-0">
                <h4 class="font-semibold text-gray-900 break-words">
                  {{ level.name }}
                </h4>
              </div>
            </div>
            <div
              class="flex w-full items-center justify-between gap-3 sm:ml-4 sm:w-auto sm:justify-end"
            >
              <div
                v-if="!hasUsableLicenseForLevel(level)"
                class="flex items-center gap-2"
              >
                <span
                  class="whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600"
                >
                  Try Free
                </span>
              </div>
              <div
                class="w-6 h-6 rounded-full border flex items-center justify-center transition-colors"
                :class="
                  isLevelSelected(level.id!)
                    ? 'bg-primary border-primary text-white'
                    : 'border-primary/30 text-transparent group-hover:border-primary/50'
                "
              >
                <BaseIconCheck class="w-4 h-4" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div
        v-if="!isLoadingCurriculumOptions && shouldShowSuggestedLevels"
        class="flex items-center gap-3"
      >
        <div class="h-px bg-gray-200 flex-1"></div>
        <h3 class="text-xs font-bold text-gray-500 uppercase tracking-wide">
          ALL
        </h3>
        <div class="h-px bg-gray-200 flex-1"></div>
      </div>

      <template v-if="!isLoadingCurriculumOptions">
        <div
          v-for="entry in visibleCategoryLevelMap"
          :key="entry.category.id"
          class="space-y-2"
        >
          <h3
            class="text-xs font-bold text-gray-700 uppercase tracking-wide px-1"
          >
            {{ entry.category.title }}
          </h3>

          <div class="space-y-2">
            <div
              v-for="level in entry.levels"
              :key="level.id"
              class="p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-sm flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between group"
              :class="
                isLevelSelected(level.id!)
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                  : 'border-gray-100 hover:border-primary/30 hover:bg-gray-50/70'
              "
              @click="selectLevel(level.id!)"
            >
              <div class="flex w-full items-center gap-4 min-w-0 sm:flex-1">
                <div
                  class="w-11 h-11 rounded-lg flex-shrink-0 flex items-center justify-center transition-colors"
                  :class="
                    isLevelSelected(level.id!)
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-400 group-hover:bg-primary/10 group-hover:text-primary'
                  "
                >
                  <BaseIconListChecks class="w-5 h-5" />
                </div>
                <div class="min-w-0">
                  <h4 class="font-semibold text-gray-900 break-words">
                    {{ level.name }}
                  </h4>
                </div>
              </div>
              <div
                class="flex w-full items-center justify-between gap-3 sm:ml-4 sm:w-auto sm:justify-end"
              >
                <div
                  v-if="!hasUsableLicenseForLevel(level)"
                  class="flex items-center gap-2"
                >
                  <span
                    class="whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600"
                  >
                    Try Free
                  </span>
                </div>
                <div
                  class="w-6 h-6 rounded-full border flex items-center justify-center transition-colors"
                  :class="
                    isLevelSelected(level.id!)
                      ? 'bg-primary border-primary text-white'
                      : 'border-gray-300 text-transparent group-hover:border-primary/40'
                  "
                >
                  <BaseIconCheck class="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div
        v-if="
          !isLoadingCurriculumOptions && visibleCategoryLevelMap.length === 0
        "
        class="text-center py-12 px-6"
      >
        <div
          class="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300"
        >
          <BaseIconSearch class="w-8 h-8" />
        </div>
        <h3 class="font-bold text-gray-900 mb-1">No curriculum found</h3>
        <p class="text-sm text-gray-500 max-w-[240px] mx-auto">
          We couldn't find any curriculum matching "{{ searchQuery }}"
        </p>
        <BaseButton
          v-if="searchQuery"
          variant="text"
          size="sm"
          class="mt-2"
          @click="searchQuery = ''"
        >
          Clear search
        </BaseButton>
      </div>
    </div>

    <div
      class="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-4 bg-white"
    >
      <p class="text-sm text-gray-600 truncate">
        {{ selectedLevel?.name ?? "No curriculum selected" }}
      </p>
      <div class="flex items-center gap-2">
        <BaseTextButton color="danger" @click="cancel">
          {{ cancelLabel }}
        </BaseTextButton>
        <BaseButton :show-spinner="isSaving" @click="saveCurriculum">
          Save Curriculum
        </BaseButton>
      </div>
    </div>
  </div>
</template>
